import { useState, useMemo, useCallback } from "react";

// Normalize unit strings to standard format
const normalizeUnit = (unit, type) => {
  const cleanUnit = unit?.trim().toLowerCase() || "";

  if (type === "temperature") {
    // Normalize to °F or °C
    if (cleanUnit.includes("f") || cleanUnit === "fahrenheit") return "°F";
    if (cleanUnit.includes("c") || cleanUnit === "celsius") return "°C";
  }

  if (type === "pressure") {
    // Normalize to standard pressure units
    if (cleanUnit === "psia" || cleanUnit === "psi") return "psia";
    if (cleanUnit === "bar") return "bar";
    if (cleanUnit === "kpa") return "kPa";
    if (cleanUnit === "mbar") return "mbar";
  }

  if (type === "flow") {
    // Normalize to standard flow units
    if (cleanUnit === "gpm") return "GPM";
    if (cleanUnit === "lpm") return "LPM";
  }

  return unit; // Return original if can't normalize
};

// Unit conversion definitions
const UNIT_CONVERSIONS = {
  temperature: {
    units: ["°F", "°C"],
    convert: (value, fromUnit, toUnit) => {
      // Normalize units first
      const from = normalizeUnit(fromUnit, "temperature");
      const to = normalizeUnit(toUnit, "temperature");

      if (from === to) return value;
      if (from === "°F" && to === "°C") {
        return (value - 32) * (5 / 9);
      }
      if (from === "°C" && to === "°F") {
        return (value * 9) / 5 + 32;
      }
      return value;
    },
  },
  pressure: {
    units: ["psia", "bar", "kPa"],
    convert: (value, from, to) => {
      if (from === to) return value;

      // Convert to psia first
      let inPsia = value;
      if (from === "bar") inPsia = value * 14.5038;
      if (from === "kPa") inPsia = value * 0.145038;

      // Convert from psia to target
      if (to === "psia") return inPsia;
      if (to === "bar") return inPsia / 14.5038;
      if (to === "kPa") return inPsia / 0.145038;

      return value;
    },
  },
  flow: {
    units: ["GPM", "LPM"],
    convert: (value, from, to) => {
      if (from === to) return value;

      // Convert to LPM first
      let inLPM = value;
      if (from === "GPM") inLPM = value * 3.78541;

      // Convert from LPM to target
      if (to === "LPM") return inLPM;
      if (to === "GPM") return inLPM / 3.78541;

      return value;
    },
  },
};

// Identify unit type from unit string
const getUnitType = (unit) => {
  const cleanUnit = unit?.trim().toLowerCase() || "";

  // Temperature - check for various temperature unit formats
  if (
    cleanUnit === "°f" ||
    cleanUnit === "f" ||
    cleanUnit === "°c" ||
    cleanUnit === "c" ||
    cleanUnit === "celsius" ||
    cleanUnit === "fahrenheit" ||
    cleanUnit === "degf" ||
    cleanUnit === "degc" ||
    cleanUnit.includes("°f") ||
    cleanUnit.includes("°c")
  ) {
    return "temperature";
  }

  // Pressure - check for various pressure unit formats
  if (
    cleanUnit === "psia" ||
    cleanUnit === "bar" ||
    cleanUnit === "kpa" ||
    cleanUnit === "mbar" ||
    cleanUnit === "psi" ||
    cleanUnit.includes("psi") ||
    cleanUnit.includes("bar")
  ) {
    return "pressure";
  }

  // Flow - check for various flow unit formats
  if (
    cleanUnit === "gpm" ||
    cleanUnit === "lpm" ||
    cleanUnit.includes("gpm") ||
    cleanUnit.includes("lpm")
  ) {
    return "flow";
  }

  return null;
};

export const useUnitConversion = (chartData) => {
  // Track unit preferences per unit type - load from localStorage on mount
  const [unitPreferences, setUnitPreferences] = useState(() => {
    try {
      const saved = localStorage.getItem("chartUnitPreferences");
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error(
        "Failed to load unit preferences from localStorage:",
        error,
      );
      return {};
    }
  });

  // Get available unit types from chart data
  const availableUnitTypes = useMemo(() => {
    const typesMap = new Map();

    console.log("useUnitConversion Debug - Processing chart data:", {
      chartDataLength: chartData.length,
      chartDataSample: chartData.slice(0, 3).map((s) => ({
        key: s.key,
        name: s.name,
        unit: s.unit,
        unitType: getUnitType(s.unit),
      })),
    });

    chartData.forEach((series) => {
      const unitType = getUnitType(series.unit);
      console.log(
        `useUnitConversion Debug - Series "${series.name}" (${series.key}): unit="${series.unit}" -> type=${unitType}`,
      );

      if (unitType && UNIT_CONVERSIONS[unitType]) {
        // Track the original units from the data
        if (!typesMap.has(unitType)) {
          typesMap.set(unitType, { originalUnit: series.unit });
        }
      }
    });

    const result = Array.from(typesMap.entries()).map(([type, info]) => ({
      type,
      options: UNIT_CONVERSIONS[type].units,
      current: unitPreferences[type] || info.originalUnit, // Default to original unit
    }));

    console.log("useUnitConversion Debug - Available unit types:", result);

    return result;
  }, [chartData, unitPreferences]);

  // Set unit preference for a specific type and save to localStorage
  const setUnitPreference = useCallback((unitType, targetUnit) => {
    setUnitPreferences((prev) => {
      const newPreferences = {
        ...prev,
        [unitType]: targetUnit,
      };

      // Save to localStorage
      try {
        localStorage.setItem(
          "chartUnitPreferences",
          JSON.stringify(newPreferences),
        );
      } catch (error) {
        console.error(
          "Failed to save unit preferences to localStorage:",
          error,
        );
      }

      return newPreferences;
    });
  }, []);

  // Convert chart data based on unit preferences
  const convertedChartData = useMemo(() => {
    return chartData.map((series) => {
      const unitType = getUnitType(series.unit);

      if (!unitType || !unitPreferences[unitType]) {
        return series; // No conversion needed
      }

      const targetUnit = unitPreferences[unitType];
      const converter = UNIT_CONVERSIONS[unitType];

      if (series.unit === targetUnit) {
        return series; // Already in target unit
      }

      // Convert all data points
      const convertedData = series.data.map((point) => ({
        ...point,
        y: converter.convert(point.y, series.unit, targetUnit),
      }));

      return {
        ...series,
        unit: targetUnit,
        data: convertedData,
        originalUnit: series.unit, // Keep track of original
      };
    });
  }, [chartData, unitPreferences]);

  return {
    convertedChartData,
    availableUnitTypes,
    setUnitPreference,
    unitPreferences,
  };
};
